import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../../services/api';
import { exportToCsv } from '../../utils/helpers';
import { useAppContext } from '../../context/AppContext';

const VideoDescriptors = ({ programId }) => { // Accept programId as prop
  const { t } = useAppContext();
  const [descriptorsData, setDescriptorsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');

  // Fetch descriptors data (wrapped in useCallback)
  const fetchDescriptors = useCallback(async () => {
    if (!programId) return; // Use programId prop

    setIsLoading(true);
    setError(null);
    try {
      const data = await apiService.getDescriptors(programId); // Use programId prop
      setDescriptorsData(data || []);
    } catch (err) {
      console.error('Failed to fetch descriptors data:', err);
      setError(t('videoDescriptors.error'));
      setDescriptorsData([]);
    } finally {
      setIsLoading(false);
    }
  }, [programId]); // Dependency on programId

  // Fetch data on mount or when programId changes
  useEffect(() => {
    fetchDescriptors();
  }, [fetchDescriptors]);

  // Extract unique descriptor types from the data
  const descriptorTypes = [
    { id: 'all', label: t('videoDescriptors.allDescriptors') },
    ...Array.from(
      new Set(descriptorsData.map(desc => desc.typeName))
    ).map(typeName => ({
      id: typeName,
      label: typeName
    }))
  ];

  // Filter descriptors based on selected category
  const filteredDescriptors = activeCategory === 'all'
    ? descriptorsData
    : descriptorsData.filter(desc => desc.typeName === activeCategory);

  // Get weight class based on value (weight is 1-10)
  const getWeightClass = (value) => {
    if (value >= 9) return 'bg-green-100 text-green-800';
    if (value >= 7) return 'bg-blue-100 text-blue-800';
    if (value >= 5) return 'bg-yellow-100 text-yellow-800';
    return 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="w-full p-4">
      <div className="mb-6">
        <h2 className="text-lg font-semibold mb-2">{t('videoDescriptors.title')}</h2>
        <p className="text-sm text-gray-600 mb-4">
          {t('videoDescriptors.description')}
        </p>
      </div>

      {/* Render loading state */}
      {isLoading && (
        <div className="w-full h-64 flex justify-center items-center">
          <div className="flex flex-col items-center">
            <svg className="animate-spin h-10 w-10 text-blue-500 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-gray-600">{t('videoDescriptors.loading')}</span>
          </div>
        </div>
      )}

      {/* Render error state */}
      {error && !isLoading && (
        <div className="w-full h-64 flex justify-center items-center">
          <div className="text-red-500 text-center">
            <p className="mb-4">{t('videoDescriptors.error')}</p>
            <button
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              onClick={fetchDescriptors} // Retry fetch
            >
              {t('programTitles.tryAgain')}
            </button>
          </div>
        </div>
      )}

      {/* Render empty state */}
      {!isLoading && !error && descriptorsData.length === 0 && (
        <div className="w-full h-64 flex justify-center items-center">
          <div className="text-gray-500 text-center">
            <p>{t('videoDescriptors.noDescriptors')}</p>
          </div>
        </div>
      )}

      {/* Render content when data is available */}
      {!isLoading && !error && descriptorsData.length > 0 && (
        <>
          <div className="flex mb-6 overflow-x-auto">
            {descriptorTypes.map(category => (
              <button
                key={category.id}
                className={`px-4 py-2 text-xs whitespace-nowrap ${
                  activeCategory === category.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                } mr-2 rounded`}
                onClick={() => setActiveCategory(category.id)}
              >
                {category.label}
              </button>
            ))}
          </div>

          <table className="w-full text-sm">
            <thead className="bg-gray-100 text-left">
              <tr>
                <th className="px-4 py-2 font-medium text-gray-600">{t('videoDescriptors.columns.descriptor')}</th>
                <th className="px-4 py-2 font-medium text-gray-600">{t('videoDescriptors.columns.type')}</th>
                <th className="px-4 py-2 font-medium text-gray-600">{t('videoDescriptors.columns.order')}</th>
                <th className="px-4 py-2 font-medium text-gray-600">{t('videoDescriptors.columns.weight')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredDescriptors.map(descriptor => (
                <tr key={descriptor.descriptorId} className="border-t border-gray-200 hover:bg-blue-50">
                  <td className="px-4 py-3 font-medium text-left">{descriptor.descriptorName}</td>
                  <td className="px-4 py-3 text-left">{descriptor.typeName}</td>
                  <td className="px-4 py-3 text-left">{descriptor.ord}</td>
                  <td className="px-4 py-3 text-left">
                    <div className="flex items-center">
                      <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full"
                          style={{ width: `${(descriptor.weight / 10) * 100}%` }}
                        ></div>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded ${getWeightClass(descriptor.weight)}`}>
                        {descriptor.weight}
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="mt-6 flex justify-between text-xs text-gray-500">
            <div>
              {t('videoDescriptors.showing', { count: filteredDescriptors.length })}
            </div>
            <div className="flex space-x-2">
              <button
                className={`px-3 py-1 border border-gray-300 rounded-sm ${descriptorsData.length === 0 ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
                onClick={() => exportToCsv(descriptorsData, `${programId}_descriptors.csv`)}
                disabled={descriptorsData.length === 0}
              >
                {t('videoDescriptors.export')}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default VideoDescriptors;
